import type { CompoundEntityRef } from '@backstage/catalog-model';

function splitSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/**
 * TechDocs reader pages are reachable at TWO different route shapes that
 * both render the same reader page for the same entity:
 *   - /catalog/<namespace>/<kind>/<name>/docs/<rest...> (docs tab nested
 *     inside the entity's catalog page)
 *   - /docs/<namespace>/<kind>/<name>/<rest...> (standalone top-level
 *     TechDocs route, reached e.g. via search results)
 * Path restrictions in config are meant to be relative to that entity's own
 * docs root ("/publications", not the full app route) - this strips
 * whichever of the two entity-root prefixes is present so restrictions read
 * the way an author would expect: as if the entity's docs were the root of
 * the site. Trying both (rather than picking one) is deliberate - assuming
 * only one previously meant restrictions silently matched nothing whenever
 * a page was reached via the other route.
 */
export function getRelativeDocsPath(
  pathname: string,
  entityRef: CompoundEntityRef,
): string {
  const roots = [
    `/catalog/${entityRef.namespace}/${entityRef.kind}/${entityRef.name}/docs`,
    `/docs/${entityRef.namespace}/${entityRef.kind}/${entityRef.name}`,
  ].map(root => root.toLowerCase());
  const lowerPathname = pathname.toLowerCase();
  const matchedRoot = roots.find(root => lowerPathname.startsWith(root));
  if (matchedRoot) {
    return pathname.slice(matchedRoot.length) || '/';
  }
  return pathname;
}

/**
 * True if `currentPath`'s segments start with `restrictionPath`'s segments,
 * in order. Segment-aware on purpose: a naive string-prefix check would
 * let "/publications" incorrectly match an unrelated "/publications-archive"
 * page. Comparing whole segments avoids that, while still letting one
 * restriction cover either a single document or an entire folder, purely
 * based on how many segments the caller provides.
 */
export function pathStartsWithSegments(
  currentPath: string,
  restrictionPath: string,
): boolean {
  const current = splitSegments(currentPath);
  const restriction = splitSegments(restrictionPath);
  // A restriction of "/" (zero segments) means "the docs root page itself",
  // not "any page" - it must only match when the current page is also the
  // root, otherwise every restriction list containing "/" would silently
  // become unrestricted (matching every nested page too).
  if (restriction.length === 0) return current.length === 0;
  if (restriction.length > current.length) return false;
  return restriction.every(
    (segment, i) => segment.toLowerCase() === current[i].toLowerCase(),
  );
}

export function matchesAnyPathRestriction(
  currentPath: string,
  restrictions: string[],
): boolean {
  return restrictions.some(restriction =>
    pathStartsWithSegments(currentPath, restriction),
  );
}
